/**
 * Usage Statistics Modal
 * Display promotion usage analytics
 */

import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';

interface UsageStatisticsModalProps {
  promotionId: number;
  onClose: () => void;
}

interface UsageStatistics {
  totalUses: number;
  totalDiscountGiven: number;
  totalOrderValue: number;
  uniqueClients: number;
  averageDiscount: number;
  averageOrderValue: number;
}

interface PromotionInfo {
  id: number;
  code: string;
  name: string;
  totalRedemptions: number;
  totalDiscountGiven: number;
  maxUses: number | null;
  expiresAt: string | null;
}

export const UsageStatisticsModal: React.FC<UsageStatisticsModalProps> = ({
  promotionId,
  onClose,
}) => {
  const [promotion, setPromotion] = useState<PromotionInfo | null>(null);
  const [statistics, setStatistics] = useState<UsageStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsageStatistics();
  }, [promotionId]);

  const loadUsageStatistics = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await adminApi.get(`/promotions/${promotionId}/usage`);
      setPromotion(response.data.promotion);
      setStatistics(response.data.statistics);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load usage statistics');
      console.error('Error loading usage statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateUsagePercentage = () => {
    if (!promotion?.maxUses) return null;
    return Math.round((promotion.totalRedemptions / promotion.maxUses) * 100);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800/80 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Usage Statistics</h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : error ? (
            <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          ) : (
            <>
              {/* Promotion Info */}
              {promotion && (
                <div className="bg-slate-700/50/50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{promotion.name}</h3>
                      <p className="text-sm text-slate-400 font-mono mt-1">{promotion.code}</p>
                    </div>
                    <div className="text-right">
                      {promotion.maxUses && (
                        <p className="text-sm text-slate-400">
                          {promotion.totalRedemptions} / {promotion.maxUses} uses (
                          {calculateUsagePercentage()}%)
                        </p>
                      )}
                      {promotion.expiresAt && (
                        <p className="text-sm text-slate-400 mt-1">
                          Expires: {formatDate(promotion.expiresAt)}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Usage Progress Bar */}
                  {promotion.maxUses && (
                    <div className="mt-4">
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            calculateUsagePercentage()! >= 100
                              ? 'bg-red-500'
                              : calculateUsagePercentage()! >= 75
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          }`}
                          style={{
                            width: `${Math.min(calculateUsagePercentage()!, 100)}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Statistics Grid */}
              {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Total Uses */}
                  <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-orange-500/30 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400 uppercase tracking-wide">
                          Total Uses
                        </p>
                        <p className="text-3xl font-bold text-white mt-2">
                          {statistics.totalUses}
                        </p>
                      </div>
                      <div className="text-orange-400">
                        <svg
                          className="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Total Discount Given */}
                  <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400 uppercase tracking-wide">
                          Total Discount Given
                        </p>
                        <p className="text-3xl font-bold text-white mt-2">
                          ${statistics.totalDiscountGiven.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-green-400">
                        <svg
                          className="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Total Order Value */}
                  <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/30 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400 uppercase tracking-wide">
                          Total Order Value
                        </p>
                        <p className="text-3xl font-bold text-white mt-2">
                          ${statistics.totalOrderValue.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-purple-400">
                        <svg
                          className="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Unique Clients */}
                  <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 border border-yellow-500/30 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400 uppercase tracking-wide">
                          Unique Clients
                        </p>
                        <p className="text-3xl font-bold text-white mt-2">
                          {statistics.uniqueClients}
                        </p>
                      </div>
                      <div className="text-yellow-400">
                        <svg
                          className="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Average Discount */}
                  <div className="bg-gradient-to-br from-red-600/20 to-red-800/20 border border-red-500/30 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400 uppercase tracking-wide">
                          Average Discount
                        </p>
                        <p className="text-3xl font-bold text-white mt-2">
                          ${statistics.averageDiscount.toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Per use</p>
                      </div>
                      <div className="text-red-400">
                        <svg
                          className="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Average Order Value */}
                  <div className="bg-gradient-to-br from-indigo-600/20 to-indigo-800/20 border border-indigo-500/30 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-400 uppercase tracking-wide">
                          Average Order Value
                        </p>
                        <p className="text-3xl font-bold text-white mt-2">
                          ${statistics.averageOrderValue.toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Per order</p>
                      </div>
                      <div className="text-indigo-400">
                        <svg
                          className="w-12 h-12"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
